import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { Route, Plus, Trash2, Play, MapPin, Calendar, AlertTriangle } from 'lucide-react';
import { formatDateOnly } from '../lib/utils';

const STATUS_LABEL = {
  rascunho: 'Rascunho',
  em_andamento: 'Em andamento',
  finalizada: 'Finalizada',
};

const STATUS_COLOR = {
  rascunho: 'bg-amber-100 text-amber-800',
  em_andamento: 'bg-emerald-100 text-emerald-800',
  finalizada: 'bg-slate-100 text-slate-600',
};

const WEEKDAY_PT = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];

/** Rótulo do dia a partir de YYYY-MM-DD, sem deslocar por timezone. */
function labelDia(dataISO) {
  if (!dataISO) return 'Sem data';
  const m = String(dataISO).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return formatDateOnly(dataISO);
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const date = new Date(y, mo - 1, d);
  const weekday = WEEKDAY_PT[date.getDay()] || '';
  return `${formatDateOnly(dataISO)}${weekday ? ` · ${weekday}` : ''}`;
}

/** Agrupa rotas por data (mais recente primeiro); dentro do dia, por técnico. */
function agruparRotasPorDia(rotas) {
  const sorted = [...rotas].sort((a, b) => {
    const da = String(a.data || '');
    const db = String(b.data || '');
    if (da !== db) return db.localeCompare(da);
    return String(a.tecnico_nome || '').localeCompare(String(b.tecnico_nome || ''), 'pt-BR');
  });
  const groups = [];
  const byDate = new Map();
  for (const rota of sorted) {
    const key = rota.data || '_sem_data';
    if (!byDate.has(key)) {
      const group = { data: rota.data || null, rotas: [] };
      byDate.set(key, group);
      groups.push(group);
    }
    byDate.get(key).rotas.push(rota);
  }
  return groups;
}

const Rotas = () => {
  const navigate = useNavigate();
  const [rotas, setRotas] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadRotas = async () => {
    try {
      const response = await api.get('/rotas');
      setRotas(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error('Erro ao carregar rotas');
      setRotas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let lastFetchAt = Date.now();
    loadRotas();
    const recarregarSeVisivel = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastFetchAt < 3000) return;
      lastFetchAt = now;
      loadRotas();
    };
    window.addEventListener('focus', recarregarSeVisivel);
    document.addEventListener('visibilitychange', recarregarSeVisivel);
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') {
        lastFetchAt = Date.now();
        loadRotas();
      }
    }, 30000);
    return () => {
      window.removeEventListener('focus', recarregarSeVisivel);
      document.removeEventListener('visibilitychange', recarregarSeVisivel);
      clearInterval(id);
    };
  }, []);

  const rotasPorDia = useMemo(() => agruparRotasPorDia(rotas), [rotas]);

  const handleIniciar = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await api.post(`/rotas/${id}/iniciar`);
      toast.success('Rota iniciada!');
      loadRotas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao iniciar rota');
    }
  };

  const temRelatorioFeito = (rota) =>
    (rota.visitas || []).some((v) => v.realizado && v.cliente_id);

  const handleDelete = async (e, rota) => {
    e.preventDefault();
    e.stopPropagation();
    if (temRelatorioFeito(rota)) {
      toast.error('Esta rota tem relatório feito. Não é possível excluir — apenas alterar.');
      navigate(`/rotas/${rota.id}`);
      return;
    }
    if (!window.confirm('Deseja realmente excluir esta rota?')) return;
    try {
      await api.delete(`/rotas/${rota.id}`);
      toast.success('Rota excluída!');
      loadRotas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir rota');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="rotas-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <Route className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Rotas</h1>
            <p className="text-slate-600">Crie rotas para o motorista e acompanhe no mapa</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/mapa-tecnicos"
            className="bg-slate-100 text-slate-700 px-5 py-3 rounded-xl font-medium hover:bg-slate-200 transition-all flex items-center gap-2"
          >
            <MapPin className="w-5 h-5" /> Mapa Técnicos
          </Link>
          <Link
            to="/rotas/nova"
            className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transition-all flex items-center gap-2"
            data-testid="nova-rota-link"
          >
            <Plus className="w-5 h-5" /> Nova Rota
          </Link>
        </div>
      </div>

      {rotas.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm text-center py-12">
          <Route className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Nenhuma rota cadastrada</p>
          <Link to="/rotas/nova" className="inline-flex items-center gap-2 mt-4 text-emerald-600 font-medium hover:underline">
            <Plus className="w-5 h-5" /> Criar primeira rota
          </Link>
        </div>
      ) : (
        <div className="space-y-6" data-testid="rotas-por-dia">
          {rotasPorDia.map((grupo) => (
            <section
              key={grupo.data || '_sem_data'}
              className="bg-white rounded-2xl shadow-sm overflow-hidden"
              data-testid={`rotas-dia-${grupo.data || 'sem-data'}`}
            >
              <div className="flex items-center justify-between gap-3 px-6 py-4 bg-slate-50 border-b border-slate-200">
                <div className="flex items-center gap-2 min-w-0">
                  <Calendar className="w-5 h-5 text-indigo-500 shrink-0" />
                  <h2 className="text-base font-semibold text-slate-900 truncate">
                    {labelDia(grupo.data)}
                  </h2>
                </div>
                <span className="text-sm text-slate-500 shrink-0">
                  {grupo.rotas.length} {grupo.rotas.length === 1 ? 'rota' : 'rotas'}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-slate-100">
                    <tr>
                      <th className="text-left py-3 px-6 text-sm font-medium text-slate-600">Técnico</th>
                      <th className="text-left py-3 px-6 text-sm font-medium text-slate-600">Carro</th>
                      <th className="text-left py-3 px-6 text-sm font-medium text-slate-600">Status</th>
                      <th className="text-left py-3 px-6 text-sm font-medium text-slate-600">KM</th>
                      <th className="text-left py-3 px-6 text-sm font-medium text-slate-600">Visitas</th>
                      <th className="text-right py-3 px-6 text-sm font-medium text-slate-600">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grupo.rotas.map((rota) => (
                      <tr
                        key={rota.id}
                        onClick={() => navigate(`/rotas/${rota.id}`)}
                        className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer"
                      >
                        <td className="py-4 px-6 text-sm text-slate-900 font-medium">{rota.tecnico_nome}</td>
                        <td className="py-4 px-6 text-sm text-slate-600">{rota.carro_placa || '—'}</td>
                        <td className="py-4 px-6">
                          <span className={`inline-flex px-2 py-1 rounded-lg text-xs font-medium ${STATUS_COLOR[rota.status] || STATUS_COLOR.rascunho}`}>
                            {STATUS_LABEL[rota.status || 'rascunho'] || rota.status}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-sm text-slate-900 font-medium">
                          <span className="inline-flex items-center gap-1">
                            {Number(rota.km_total || 0).toFixed(0)} km
                            {rota.reconciliacao_pendente_admin && (
                              <AlertTriangle
                                className="w-4 h-4 text-amber-500"
                                title="Odômetro e trajeto GPS não fecham: revise o KM desta rota"
                              />
                            )}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-sm text-slate-600">
                          {rota.visitas?.length || 0}
                          {rota.visitas?.filter((v) => v.realizado && v.cliente_id).length > 0 && (
                            <span className="text-emerald-600 ml-1">({rota.visitas.filter((v) => v.realizado && v.cliente_id).length} feitos)</span>
                          )}
                        </td>
                        <td className="py-4 px-6 text-right" onClick={(e) => e.stopPropagation()}>
                          {rota.status === 'rascunho' && (
                            <button
                              onClick={(e) => handleIniciar(e, rota.id)}
                              className="text-emerald-600 hover:text-emerald-700 p-2 rounded-lg hover:bg-emerald-50 transition-colors inline-flex items-center gap-1"
                              title="Iniciar rota"
                            >
                              <Play className="w-4 h-4" /> Iniciar
                            </button>
                          )}
                          {temRelatorioFeito(rota) ? (
                            <span
                              className="text-slate-400 text-xs ml-2 inline-block align-middle"
                              title="Com relatório feito — abra a rota para alterar"
                            >
                              Só alterar
                            </span>
                          ) : (
                            <button
                              onClick={(e) => handleDelete(e, rota)}
                              className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors ml-1"
                              title="Excluir rota"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default Rotas;
